import { ForRentByType, type GetUserPropertyDetailsResponseWebDto } from '@zg-rentals/ts-rental-manager-api-client';

import propertyUtils from '../../utils/propertyUtils';
import typeCheckUtils from '../../utils/typeCheckUtils';

export const SCREENING_CRITERIA_INDEX = '4';
export const SCROLL_ANIMATION_DELAY = 100;

const requiredFieldNames = {
  contactEmail: 'email',
  contactName: 'contactName',
  contactPhone: 'phone',
  forRentBy: 'forRentBy',
  isApplicationsAccepted: 'isApplicationsAccepted',
  leaseDuration: 'leaseDuration',
} as const;

const contactSectionRequiredFields = [
  requiredFieldNames.contactEmail,
  requiredFieldNames.contactName,
  requiredFieldNames.contactPhone,
  requiredFieldNames.forRentBy,
  requiredFieldNames.isApplicationsAccepted,
  requiredFieldNames.leaseDuration,
] as const;

const getRequiredRoomForRentFieldErrors = ({
  newRequiredFieldsErrors,
  listingDetails,
  isPetsAllowedEmpty,
}: {
  newRequiredFieldsErrors: Record<string, boolean>;
  listingDetails: GetUserPropertyDetailsResponseWebDto;
  isPetsAllowedEmpty: boolean;
}) => {
  const {
    fullDescription,
    forRentByType,
    roomForRent: { hasPermissionToCreateListing },
  } = listingDetails;

  // property information check
  if (!fullDescription) {
    newRequiredFieldsErrors.floorplans = true;
  }

  if (isPetsAllowedEmpty) {
    newRequiredFieldsErrors.rentDetails = true;
  }

  // final details check
  if (forRentByType === ForRentByType.TENANT && !hasPermissionToCreateListing) {
    newRequiredFieldsErrors.contact = true;
  }

  return newRequiredFieldsErrors;
};

export const getRequiredFieldsErrors = ({
  listingDetails,
  feeAmount,
  hasTouringSectionErrors = false,
  canAccessScreeningCriteriaInListingFlow = false,
  isApplicationsUpdateRestricted = false,
}: {
  listingDetails?: GetUserPropertyDetailsResponseWebDto;
  feeAmount: number | null;
  hasTouringSectionErrors?: boolean;
  canAccessScreeningCriteriaInListingFlow?: boolean;
  isApplicationsUpdateRestricted?: boolean;
}): Record<string, boolean> => {
  if (!listingDetails) return {};

  const { active, amenities, floorPlans = [], isFeed, petsAllowed, laundry } = listingDetails;

  const { lowPrice, highPrice, numBedrooms, numBathrooms } = floorPlans[0] || {};
  const price = lowPrice ?? highPrice;
  const isRoomForRent = propertyUtils.isRoomForRent({ listing: listingDetails });

  if (active || isFeed) return {};

  const isPetsAllowedEmpty = propertyUtils.getIsPetsAllowedEmpty(petsAllowed);

  let newRequiredFieldsErrors: Record<string, boolean> = {};

  newRequiredFieldsErrors.rentDetails = !price;
  newRequiredFieldsErrors.screeningCriteria = canAccessScreeningCriteriaInListingFlow && isPetsAllowedEmpty;

  // contact is the final details section
  newRequiredFieldsErrors.contact = contactSectionRequiredFields.some((field) => {
    if (field === requiredFieldNames.isApplicationsAccepted) {
      // NOTE: listingDetails.isApplicationsAccepted can be `null` due to legacy
      return (
        !isRoomForRent &&
        !isApplicationsUpdateRestricted &&
        (listingDetails[field] === null || (!listingDetails[field] && !typeCheckUtils.isNumeric(feeAmount)))
      );
    }

    return !listingDetails[field];
  });

  if (hasTouringSectionErrors) {
    newRequiredFieldsErrors.contact = true;
  }

  const isInteriorRequirementFulfilled = propertyUtils.checkIsAmenityFieldFulfilled({
    amenityField: amenities?.interior,
  });

  newRequiredFieldsErrors.amenities = !laundry || !isInteriorRequirementFulfilled;
  newRequiredFieldsErrors.floorplans = numBedrooms === undefined || numBathrooms === undefined;

  if (isRoomForRent) {
    newRequiredFieldsErrors = getRequiredRoomForRentFieldErrors({
      newRequiredFieldsErrors,
      listingDetails,
      isPetsAllowedEmpty,
    });
  }

  // Granular keys — only for the error guide UI
  // property information accordion section
  newRequiredFieldsErrors.beds = numBedrooms === undefined;
  newRequiredFieldsErrors.baths = numBathrooms === undefined;
  if (isRoomForRent && !listingDetails.fullDescription) {
    newRequiredFieldsErrors.roomDescription = true;
  }

  // rent details accordion section
  newRequiredFieldsErrors.monthlyRent = !price;
  if (isRoomForRent) {
    newRequiredFieldsErrors.rentDetailsPets = isPetsAllowedEmpty;
    newRequiredFieldsErrors.screeningCriteriaPetsAllowed = false;
  }

  // amenities accordion section
  newRequiredFieldsErrors.interiorAmenities = !laundry || !isInteriorRequirementFulfilled;

  // screening criteria accordion section
  if (!isRoomForRent) {
    newRequiredFieldsErrors.screeningCriteriaPetsAllowed = isPetsAllowedEmpty;
  }

  // final details accordion section
  newRequiredFieldsErrors.contactEmail = !listingDetails[requiredFieldNames.contactEmail];
  newRequiredFieldsErrors.contactName = !listingDetails[requiredFieldNames.contactName];
  newRequiredFieldsErrors.contactPhone = !listingDetails[requiredFieldNames.contactPhone];
  newRequiredFieldsErrors.forRentBy = !listingDetails[requiredFieldNames.forRentBy];
  if (!isRoomForRent && !isApplicationsUpdateRestricted) {
    const appAccepted = listingDetails[requiredFieldNames.isApplicationsAccepted] as boolean | null;
    newRequiredFieldsErrors.isApplicationsAccepted =
      appAccepted === null || (!appAccepted && !typeCheckUtils.isNumeric(feeAmount));
  }
  newRequiredFieldsErrors.leaseDuration = !listingDetails[requiredFieldNames.leaseDuration];
  if (isRoomForRent) {
    newRequiredFieldsErrors.r4rPermissions =
      listingDetails.forRentByType === ForRentByType.TENANT && !listingDetails.roomForRent.hasPermissionToCreateListing;
  }
  if (hasTouringSectionErrors) {
    newRequiredFieldsErrors.touring = true;
  }

  return newRequiredFieldsErrors;
};
